import { useCallback, useState, useEffect } from 'react';
import {
  Button,
  CheckboxButton,
  Dropdown,
  Input,
  Title,
  TrashIcon,
} from '../../components/shared';
import { useWidth } from '../../lib/useWidth';
import LineAssessExpireDaysInput from '../../components/user-settings/LineAssessExpireDaysInput';
import { useDispatch, useSelector } from 'react-redux';
import SeedStagesSize from '../../components/user-settings/SeedStagesSize';
import {
  updateAccountSetting,
  updateUserSetting,
} from '../../store/auth/auth.actions';
import {
  addUtilData,
  deleteUtilData,
  loadUtilsData,
} from '../../store/utils/utils.actions';
import { showFeedback } from '../../store/ui/ui.actions';
import { selectUtils } from '../../store/utils/utils.selector';
import {
  selectAccount,
  selectIsAdmin,
  selectSettings,
} from '../../store/auth/auth.selector';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import OysterHarvestTypes from '../../components/farm-util/OysterHarvestTypes';
import {
  areSameFloats,
  getUnitOfSpecie,
  numToStr,
} from '../../lib/common.helpers';
import { sendSingleRequest } from '../../apis';
import { Table } from 'antd';

interface IPredictionData {
  mussel_farm_harvest_size: number | null;
  oyster_farm_harvest_size: number | null;
  mussel_average_grow_speed: number | null;
  oyster_average_grow_speed: number | null;
}

const harvestTypes = [
  'Whole Mussel',
  'Half Shell',
  'Meat',
  'Oil Product',
  'Powder Product',
];

const MusselHarvestTypeList = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const isAdmin = useSelector(selectIsAdmin);
  const checkedOptions = useSelector(selectUtils).filter(
    x => x.type === 'harvest_type',
  );
  const otherOptions = checkedOptions.filter(
    x => !harvestTypes.includes(x.name),
  );
  const [otherVal, setOtherVal] = useState('');

  const isChecked = useCallback(
    (v: string) => checkedOptions.some(x => x.name === v),
    [checkedOptions],
  );
  const toggleCheck = useCallback(
    (v: string) => {
      let item = checkedOptions.find(x => x.name === v);
      if (item) {
        dispatch(deleteUtilData(item.id));
      } else {
        dispatch(addUtilData('harvest_type', v));
      }
    },
    [checkedOptions, dispatch],
  );
  const onOtherAddClick = async () => {
    if (otherVal.length <= 0 || checkedOptions.some(x => x.name === otherVal))
      return;
    await dispatch(addUtilData('harvest_type', otherVal));
    setOtherVal('');
  };
  const onOtherDeleteClick = async (v: string) => {
    let item = checkedOptions.find(x => x.name === v);
    if (item) {
      await dispatch(deleteUtilData(item.id));
    }
  };

  return (
    <div className='mt-19 harvest-type-list mb-16'>
      <div className='d-flex mb-16 mt-27 align-center justify-between'>
        <Title size={6} color='black-3' align='default' fontWeight={500}>
          {translate(lang, '_mussel_harvest_type_list')}
        </Title>
      </div>
      <div className='ml-21'>
        {harvestTypes.map((s, i) => (
          <CheckboxButton
            key={i}
            className='mb-17 ml-0'
            label={s}
            disabled={!isAdmin}
            checked={isChecked(s)}
            onChange={e => toggleCheck(s)}
          />
        ))}
        {otherOptions.map(x => (
          <div key={x.id.toString()} className='--other-option'>
            <Input type='string' disabled={true} value={x.name} label='' />
            <button
              className='--other-delete-btn'
              disabled={!isAdmin}
              onClick={e => onOtherDeleteClick(x.name)}
            >
              <TrashIcon />
            </button>
          </div>
        ))}
        <div className='d-flex align-end mt-17'>
          <Input
            label={translate(lang, 'Other')}
            type='text'
            value={otherVal}
            required={false}
            disabled={!isAdmin}
            onChange={e => setOtherVal(e.target.value)}
          />
          <Button
            className='ml-17'
            type='fill'
            width='large'
            size={1}
            color='blue'
            disabled={!isAdmin}
            onClick={onOtherAddClick}
          >
            {translate(lang, 'Add')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const TrackSettings = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const isAdmin = useSelector(selectIsAdmin);
  const accSetting = useSelector(selectAccount);
  const track_spat = accSetting?.track_original_spat;
  const track_mortality = accSetting?.track_mortality;
  const hide_harvest_species = accSetting?.hide_harvest_species;
  const manage_shellfish_width = accSetting?.manage_shellfish_width;
  const gpsCheck = useSelector(selectSettings).show_gps_warning;

  const [disabled, setDisabled] = useState({
    gps: false,
    track_original_spat: false,
    track_mortality: false,
    hide_harvest_species: false,
    manage_shellfish_width: false,
  });

  const toggleGpsCheck = async (c: boolean) => {
    setDisabled(prv => ({ ...prv, gps: true }));
    await dispatch(updateUserSetting('show_gps_warning', c));
    setDisabled(prv => ({ ...prv, gps: false }));
  };
  const toggleCheck = async (
    key:
      | 'track_original_spat'
      | 'track_mortality'
      | 'hide_harvest_species'
      | 'manage_shellfish_width',
    c: boolean,
  ) => {
    setDisabled(prv => ({ ...prv, [key]: true }));
    const res = await dispatch(updateAccountSetting({ [key]: c }));
    setDisabled(prv => ({ ...prv, [key]: false }));
    if (res !== true) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, res?.data?.message ?? 'Server error'),
        }),
      );
    }
  };

  return (
    <>
      <div className='mb-32'>
        <div className='mb-17'>
          <Title size={6} color='black-3' align='default' fontWeight={500}>
            {translate(lang, 'Track Settings')}
          </Title>
        </div>
        <div className='ml-21'>
          <div className='mb-17'>
            <CheckboxButton
              label={translate(lang, '_show_gps_warning_msg')}
              checked={!!gpsCheck}
              onChange={e => toggleGpsCheck(e.target.checked)}
            />
          </div>
          <div className='mb-17'>
            <CheckboxButton
              label={translate(lang, 'Track Original Spats')}
              disabled={disabled.track_original_spat || !isAdmin}
              checked={track_spat}
              onChange={e =>
                toggleCheck('track_original_spat', e.target.checked)
              }
            />
          </div>
          <div className='mb-17'>
            <CheckboxButton
              label={translate(lang, 'Track Mortality')}
              disabled={disabled.track_mortality || !isAdmin}
              checked={track_mortality}
              onChange={e => toggleCheck('track_mortality', e.target.checked)}
            />
          </div>
          <div className='mb-17'>
            <CheckboxButton
              label={translate(lang, 'Hide Harvest Species')}
              disabled={disabled.hide_harvest_species || !isAdmin}
              checked={hide_harvest_species}
              onChange={e =>
                toggleCheck('hide_harvest_species', e.target.checked)
              }
            />
          </div>
          <div className='mb-17'>
            <CheckboxButton
              label={translate(lang, 'Manage shellfish width')}
              disabled={disabled.manage_shellfish_width || !isAdmin}
              checked={!!manage_shellfish_width}
              onChange={e =>
                toggleCheck('manage_shellfish_width', e.target.checked)
              }
            />
          </div>
        </div>
      </div>
    </>
  );
};

const DropperLengthSetting = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const isAdmin = useSelector(selectIsAdmin);
  const default_dropper_length =
    useSelector(selectAccount)?.default_dropper_length;

  const [useDefaultDropperLength, setUseDefaultDropperLength] = useState(
    default_dropper_length != null,
  );

  const [defaultDropperLengthInput, setDefaultDropperLengthInput] = useState(
    numToStr(default_dropper_length),
  );
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    setUseDefaultDropperLength(default_dropper_length != null);
    setDefaultDropperLengthInput(numToStr(default_dropper_length));
  }, [default_dropper_length]);

  const updateDropperLength = async (value: number | null) => {
    setDisabled(true);
    const res = await dispatch(
      updateAccountSetting({ default_dropper_length: value }),
    );
    setDisabled(false);
    if (res !== true) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, res?.data?.message ?? 'Server error'),
        }),
      );
    }
  };

  const toggleUseDefaultDropperLength = (checked: boolean) => {
    setUseDefaultDropperLength(checked);
  };

  const onDropperLengthChange = (value: string) => {
    setDefaultDropperLengthInput(value);
  };

  const updateClick = async () => {
    if (!useDefaultDropperLength) {
      await updateDropperLength(null);
      return;
    }
    const parsed = Number(defaultDropperLengthInput);
    const nextValue =
      defaultDropperLengthInput.length > 0 && !Number.isNaN(parsed)
        ? parsed
        : 0;
    await updateDropperLength(nextValue);
  };

  return (
    <div className='mb-24'>
      <div className='mb-16'>
        <Title size={6} color='black-3' align='default' fontWeight={500}>
          {translate(lang, '_len_of_droppers')}
        </Title>
      </div>
      <div className='ml-21'>
        <div className='mb-17'>
          <CheckboxButton
            label={translate(lang, '_use_default_dropper_len')}
            disabled={disabled || !isAdmin}
            checked={useDefaultDropperLength}
            onChange={e => toggleUseDefaultDropperLength(e.target.checked)}
          />
        </div>
        {useDefaultDropperLength && (
          <div className='mb-17'>
            <Input
              label={translate(lang, '_default_dropper_len')}
              type='number'
              value={defaultDropperLengthInput}
              disabled={disabled || !isAdmin}
              onChange={e => onDropperLengthChange(e.target.value)}
            />
          </div>
        )}
        <Button
          type='fill'
          width='small'
          size={0}
          color='blue'
          onClick={updateClick}
          disabled={disabled || !isAdmin}
        >
          {translate(lang, 'Update')}
        </Button>
      </div>
    </div>
  );
};

const HarvestSizeSetting = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const isAdmin = useSelector(selectIsAdmin);
  const accSetting = useSelector(selectAccount);

  const [musselMinSize, setMusselMinSize] = useState(
    accSetting?.mussel_harvest_min_size,
  );
  const [oysterMinSize, setOysterMinSize] = useState(
    accSetting?.oyster_harvest_min_size,
  );
  const [musselGrowSpeed, setMusselGrowSpeed] = useState(
    accSetting?.mussel_grow_speed,
  );
  const [oysterGrowSpeed, setOysterGrowSpeed] = useState(
    accSetting?.oyster_grow_speed,
  );
  const [disabled, setDisabled] = useState(false);
  const [prediction, setPrediction] = useState<IPredictionData | null>(null);

  useEffect(() => {
    sendSingleRequest({}, 'GET', 'api/overview/prediction', true).then(res => {
      if (res.status) {
        setPrediction(res.data);
      }
    });
  }, []);

  const updateValue = (
    key: 'mussel_size' | 'oyster_size' | 'mussel_speed' | 'oyster_speed',
    value: string,
  ) => {
    const val = value.length <= 0 ? undefined : Number(value);
    if (key === 'mussel_size') setMusselMinSize(val);
    else if (key === 'oyster_size') setOysterMinSize(val);
    else if (key === 'mussel_speed') setMusselGrowSpeed(val);
    else setOysterGrowSpeed(val);
  };
  const updateClick = async () => {
    setDisabled(true);
    await dispatch(
      updateAccountSetting({
        mussel_harvest_min_size: musselMinSize,
        oyster_harvest_min_size: oysterMinSize,
        mussel_grow_speed: musselGrowSpeed,
        oyster_grow_speed: oysterGrowSpeed,
      }),
    );
    setDisabled(false);
  };

  const sizeUnit = getUnitOfSpecie(accSetting?.display_unit, 'size_mm');
  const speedUnit = `${sizeUnit} per month`;

  return (
    <div className='mb-12'>
      <div className='mb-16'>
        <Title size={6} color='black-3' align='default' fontWeight={500}>
          {`${translate(lang, 'Size')} & ${translate(lang, 'Speed')}`}
        </Title>
      </div>
      <div className='d-flex align-start ml-21 mb-12'>
        <div className='w-100 mr-7'>
          <Input
            label={`${translate(lang, 'Mussel')} ${translate(lang, 'Minimum harvest size')}`}
            value={numToStr(musselMinSize)}
            onChange={e => updateValue('mussel_size', e.target.value)}
            type='number'
            unit={sizeUnit}
            disabled={
              !isAdmin ||
              (prediction?.mussel_farm_harvest_size != null &&
                !areSameFloats(
                  prediction.mussel_farm_harvest_size,
                  accSetting?.mussel_harvest_min_size,
                ))
            }
          />
          {prediction?.mussel_farm_harvest_size != null && (
            <p className='mt-4 mb-0' style={{ fontSize: 12, color: '#888' }}>
              {translate(
                lang,
                '_avg_mussel_harvest_size',
                prediction.mussel_farm_harvest_size.toFixed(2),
                sizeUnit,
              )}
            </p>
          )}
        </div>
        <div className='w-100 ml-7'>
          <Input
            label={`${translate(lang, 'Mussel')} ${translate(lang, 'Grow Speed')}`}
            value={numToStr(musselGrowSpeed)}
            onChange={e => updateValue('mussel_speed', e.target.value)}
            type='number'
            unit={speedUnit}
            disabled={
              !isAdmin ||
              (prediction?.mussel_average_grow_speed != null &&
                !areSameFloats(
                  30 * prediction.mussel_average_grow_speed,
                  accSetting?.mussel_grow_speed,
                ))
            }
          />
          {prediction?.mussel_average_grow_speed != null && (
            <p className='mt-4 mb-0' style={{ fontSize: 12, color: '#888' }}>
              {translate(
                lang,
                '_avg_mussel_grow_speed',
                (30 * prediction.mussel_average_grow_speed).toFixed(2),
                sizeUnit,
              )}
            </p>
          )}
        </div>
      </div>
      <div className='d-flex align-start ml-21'>
        <div className='w-100 mr-7'>
          <Input
            label={`${translate(lang, 'Oyster')} ${translate(lang, 'Minimum harvest size')}`}
            value={numToStr(oysterMinSize)}
            onChange={e => updateValue('oyster_size', e.target.value)}
            type='number'
            unit={sizeUnit}
            disabled={
              !isAdmin ||
              (prediction?.oyster_farm_harvest_size != null &&
                !areSameFloats(
                  prediction?.oyster_farm_harvest_size,
                  accSetting?.oyster_harvest_min_size,
                ))
            }
          />
          {prediction?.oyster_farm_harvest_size != null && (
            <p className='mt-4 mb-0' style={{ fontSize: 12, color: '#888' }}>
              {translate(
                lang,
                '_avg_oyster_harvest_size',
                prediction.oyster_farm_harvest_size.toFixed(2),
                sizeUnit,
              )}
            </p>
          )}
        </div>
        <div className='w-100 ml-7'>
          <Input
            label={`${translate(lang, 'Oyster')} ${translate(lang, 'Grow Speed')}`}
            value={numToStr(oysterGrowSpeed)}
            onChange={e => updateValue('oyster_speed', e.target.value)}
            type='number'
            unit={speedUnit}
            disabled={
              !isAdmin ||
              (prediction?.oyster_average_grow_speed != null &&
                !areSameFloats(
                  30 * prediction.oyster_average_grow_speed,
                  accSetting?.oyster_grow_speed,
                ))
            }
          />
          {prediction?.oyster_average_grow_speed != null && (
            <p className='mt-4 mb-0' style={{ fontSize: 12, color: '#888' }}>
              {translate(
                lang,
                '_avg_oyster_grow_speed',
                (30 * prediction.oyster_average_grow_speed).toFixed(2),
                sizeUnit,
              )}
            </p>
          )}
        </div>
      </div>
      <Button
        className='mt-16 mb-16 ml-21'
        type='fill'
        width='small'
        size={0}
        color='blue'
        onClick={updateClick}
        disabled={disabled || !isAdmin}
      >
        {translate(lang, 'Update')}
      </Button>
    </div>
  );
};

const StageLabels = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const isAdmin = useSelector(selectIsAdmin);
  const stageLabels = useSelector(selectAccount)?.stage_labels ?? [];

  const [labels, setLabels] = useState([
    ...stageLabels,
    ...[
      { label: null, color: null },
      { label: null, color: null },
      { label: null, color: null },
      { label: null, color: null },
    ].slice(stageLabels.length),
  ]);
  const [disabled, setDisabled] = useState(false);

  const updateClick = async () => {
    setDisabled(true);
    await dispatch(updateAccountSetting({ stage_labels: labels }));
    setDisabled(false);
  };

  const updateLabel = (index: number, value: string) => {
    setLabels(prv =>
      prv.map((x, j) =>
        j === index
          ? {
              ...x,
              label: value.length <= 0 ? null : value,
            }
          : x,
      ),
    );
  };

  const updateColorHex = (index: number, value: string) => {
    const trimmed = value.trim();
    const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(trimmed);
    setLabels(prv =>
      prv.map((x, j) =>
        j === index
          ? {
              ...x,
              color:
                trimmed.length === 0 ? null : isValidHex ? trimmed : x.color,
            }
          : x,
      ),
    );
  };

  const updateColorPicker = (index: number, value: string) => {
    setLabels(prv =>
      prv.map((x, j) => (j === index ? { ...x, color: value } : x)),
    );
  };

  const dataSource = labels.map((item, index) => ({
    key: index,
    index,
    ...item,
  }));

  const columns: any = [
    {
      title: translate(lang, 'Stage'),
      key: 'stage',
      render: (record: any) => `Stage ${record.index + 1}`,
      width: 100,
    },
    {
      title: translate(lang, 'Label'),
      key: 'label',
      render: (record: any) => (
        <Input
          label=''
          type='text'
          value={record.label || ''}
          disabled={disabled || !isAdmin}
          onChange={({ target }) => updateLabel(record.index, target.value)}
        />
      ),
      width: 200,
    },
    {
      title: translate(lang, 'Color (Hex)'),
      key: 'colorHex',
      render: (record: any) => (
        <Input
          label=''
          type='text'
          value={record.color || ''}
          disabled={disabled || !isAdmin}
          onChange={({ target }) => updateColorHex(record.index, target.value)}
          placeholder='#000000'
        />
      ),
      width: 150,
    },
    {
      title: translate(lang, 'Color'),
      key: 'colorPicker',
      render: (record: any) => (
        <input
          type='color'
          value={record.color ?? '#000000'}
          disabled={disabled || !isAdmin}
          onChange={({ target }) =>
            updateColorPicker(record.index, target.value)
          }
          style={{ height: 37, width: '100%', cursor: 'pointer' }}
        />
      ),
      width: 70,
    },
  ];

  return (
    <div className='mb-24 mt-24'>
      <div className='mb-16'>
        <Title size={6} color='black-3' align='default' fontWeight={500}>
          {translate(lang, 'Stage Labels')}
        </Title>
      </div>
      <div className='ml-21'>
        <Table
          rowKey='key'
          className='table table--units table--small'
          dataSource={dataSource}
          columns={columns}
          pagination={false}
        />
      </div>
      <Button
        className='mt-16 mb-16 ml-21'
        type='fill'
        width='small'
        size={0}
        color='blue'
        onClick={updateClick}
        disabled={disabled || !isAdmin}
      >
        {translate(lang, 'Update')}
      </Button>
    </div>
  );
};

const UnitSetting = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const isAdmin = useSelector(selectIsAdmin);
  const accSetting = useSelector(selectAccount);
  const unit_value = accSetting?.display_unit || 'METRIC';

  const options = [
    { id: 'METRIC', value: 'METRIC', label: 'Metric (mm, kg, km)' },
    {
      id: 'IMPERIAL',
      value: 'IMPERIAL',
      label: 'Imperial (inches, lbs, miles)',
    },
  ];
  const [disabled, setDisabled] = useState(false);

  const updateSetting = async (value: string) => {
    setDisabled(true);
    await dispatch(updateAccountSetting({ display_unit: value }));
    setDisabled(false);
  };

  return (
    <div className='mb-24'>
      <Title size={6} color='black-3' align='default' fontWeight={500}>
        {translate(lang, 'Display Unit')}
      </Title>
      <Dropdown
        options={options}
        value={unit_value}
        onChange={v => updateSetting(v)}
        disabled={disabled || !isAdmin}
      />
    </div>
  );
};

const SettingsPage = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const width = useWidth();

  useEffect(() => {
    dispatch(loadUtilsData());
  }, [dispatch]);

  return (
    <div className='content pb-32'>
      {width > 768 && (
        <Title
          className='mb-24'
          size={5}
          color='black-3'
          align='default'
          fontWeight={600}
        >
          {translate(lang, 'Setting')}
        </Title>
      )}
      <MusselHarvestTypeList />
      <OysterHarvestTypes />
      <LineAssessExpireDaysInput />
      <SeedStagesSize />
      <TrackSettings />
      <DropperLengthSetting />
      <HarvestSizeSetting />
      <StageLabels />
      <UnitSetting />
    </div>
  );
};

export default SettingsPage;
